import { useSubjectMember, useSubjectUpdate } from '@healthblocks-io/core'
import { t } from '@lingui/macro'
import { Form, Formik } from 'formik'
import { useMemo, useState } from 'react'
import { useTheme } from 'styled-components'

import { AddressAutocomplete } from '@/@bothrs-ui/web/components/forms/addressAutocomplete'
import { FormListener } from '@/@bothrs-ui/web/components/forms/FormListener'
import { Input } from '@/@bothrs-ui/web/components/forms/input'
import { Label } from '@/@bothrs-ui/web/components/forms/label'
import { FlexColumn, Margin } from '@/@bothrs-ui/web/components/layout'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { Line } from '@/@bothrs-ui/web/components/primitives/line/Line'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { formatPhoneNumber } from '@/utils/format-phone-number'
import { generateContactInformationFormSchema } from './ContactInformationForm.schema'

import type { Path } from 'ramda'

type ContactInformationFormProps = {
  actionLabel: string
  setFormValid?: React.Dispatch<React.SetStateAction<boolean>>
  onComplete?: () => void
  onBoarding?: boolean
}

const ContactInformationForm = ({
  actionLabel,
  setFormValid,
  onComplete,
  onBoarding = false,
}: ContactInformationFormProps) => {
  const [loading, setLoading] = useState<boolean>(false)
  const [canSave, setCanSave] = useState<boolean>(false)
  const theme = useTheme()
  const member = useSubjectMember()

  const [{ mutate }, updateSubject] = useSubjectUpdate()

  const ContactInformationFormSchema = generateContactInformationFormSchema()

  // Interaction Methods
  // ------------------------------------------------------------------------- /
  function handleSubmit(values: Record<string, any>) {
    setLoading(true)

    // Refresh the user before updating the actor to make sure we have the latest metadata
    mutate()
      .then((latestActor) =>
        updateSubject({
          email: values.email,
          phone: values.contactPhone,
          addresses: [
            {
              line: [values.address.streetAddress],
              city: values.address.city,
              postalCode: values.address.zipOrPostalCode,
              country: values.address.country,
            },
          ],
          meta: {
            ...latestActor?.meta,
            cellphone: values.cellphone,
          },
        }),
      )
      .then(() => {
        setLoading(false)

        if (onComplete) {
          onComplete()
        }
      })
  }

  function handleFormChange(values: Record<string, unknown>, isValid: boolean) {
    setCanSave((isValid && JSON.stringify(values) !== JSON.stringify(formInitialValues)) || onBoarding)

    if (setFormValid) {
      setFormValid(isValid)
    }
  }

  // Template variables
  // ------------------------------------------------------------------------- /
  const formInitialValues = useMemo(() => {
    const memberAddress = member?.addresses.at(0)

    return {
      email: member?.email || '',
      contactPhone: formatPhoneNumber(member?.phone),
      cellphone: formatPhoneNumber(member?.meta.cellphone),
      address: {
        streetAddress: memberAddress?.line?.at(0) || '',
        zipOrPostalCode: memberAddress?.postalCode || '',
        city: memberAddress?.city || '',
        country: memberAddress?.country || 'BE',
      },
    }
  }, [member])
  return (
    <Formik
      initialValues={formInitialValues}
      enableReinitialize
      validationSchema={ContactInformationFormSchema}
      validateOnChange
      validateOnMount
      onSubmit={handleSubmit}
    >
      {({ values, errors }) => {
        return (
          <Form id='contact-information-form'>
            <FormListener
              fields={Object.keys(values) as unknown as Path[]}
              errors={errors}
              onChange={handleFormChange}
            />

            <FlexColumn gap={theme.UI.Spacing.Space4}>
              <AddressAutocomplete
                id='address'
                label={t({
                  id: 'globals.labels.address',
                  message: 'Adres',
                })}
                placeholder={t({
                  id: 'globals.labels.addresautocomplete',
                  message: 'Zoek je adres',
                })}
                required
                setPostalCodeAndCityMismatch={(value) => {
                  console.log('setPostalCodeAndCityMismatch', value)
                }}
              />
              <Line color={theme.Colors.Neutrals.Lighter} />

              <FlexColumn gap={theme.UI.Spacing.Space2}>
                <Label color={theme.Colors.Neutrals.Darker} type='BodyMediumBold' htmlFor={'phonenumbers'}>
                  {t({
                    id: 'globals.labels.phonenumber',
                    message: 'Telefoonnummer (minstens 1) *',
                  })}
                </Label>

                <Span type='BodyMediumRegular' color={theme.Colors.Neutrals.Darkest} style={{ fontWeight: 400 }}>
                  {t({
                    id: 'globals.labels.contactnumber',
                    message: 'Vast',
                  })}
                </Span>
                <Input
                  name='contactPhone'
                  type='tel'
                  inputMode='tel'
                  placeholder='+32 0 000 00 00'
                  autoComplete='tel'
                  noMargin
                  ignoreTouched
                />

                <Span type='BodyMediumRegular' color={theme.Colors.Neutrals.Darkest} style={{ fontWeight: 400 }}>
                  {t({
                    id: 'globals.labels.phone',
                    message: 'Mobiel',
                  })}
                </Span>
                <Input
                  name='cellphone'
                  type='tel'
                  inputMode='tel'
                  placeholder='+32 000 00 00 00'
                  autoComplete='tel'
                  noMargin
                  ignoreTouched
                />
              </FlexColumn>
              <Line color={theme.Colors.Neutrals.Lighter} />

              <Input
                label={t({
                  id: 'globals.labels.email',
                  message: 'E-mailadres',
                })}
                name='email'
                type='email'
                inputMode='email'
                placeholder='voornaam.achternaam@email.be'
                autoComplete='email'
                required
                noMargin
                ignoreTouched
              />
            </FlexColumn>

            <Margin mt={theme.UI.Spacing.Space8}>
              <Button variant='Primary' type='submit' isLoading={loading} disabled={!canSave}>
                {actionLabel}
              </Button>
            </Margin>
          </Form>
        )
      }}
    </Formik>
  )
}

export { ContactInformationForm }
